import { useState } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { useAssignVedlikehold, useSetVedlikeholdStatus } from '@/services/vedlikehold';
import { nb } from '@/i18n/nb';
import type { VedlikeholdEntry, VedlikeholdStatus } from '@/types/api';
import { StatusBadge } from './StatusBadge';
import s from './Vedlikehold.module.css';

// Order shown in the control (per dugnad-status-control): the working states first,
// then «Åpen» last as the "remove assignee / start over" escape.
const STATUS_ORDER: VedlikeholdStatus[] = ['tildelt', 'utført', 'kansellert', 'åpen'];

/*
 * StatusControlModal — change a Dugnadsoppgave's status (per dugnad-status-control).
 * Tap-to-apply (no separate confirm — a status switch is a single choice), current
 * status marked. The four options map back onto the two endpoints (ADR-0028 /
 * technical-spec §API contracts):
 *   - «Åpen»                        → clear the assignee (PUT .../assignee {null})
 *   - tildelt / utført / kansellert → PATCH .../status
 * Only rendered for an ASSIGNED task; «Åpen» is what returns it to unassigned. Open to
 * any logged-in user — the server enforces the åpen ⟺ no-assignee invariant.
 */
export function StatusControlModal({
  task,
  onClose,
}: {
  task: VedlikeholdEntry;
  onClose: () => void;
}) {
  const statusMutation = useSetVedlikeholdStatus();
  const assignMutation = useAssignVedlikehold();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState<VedlikeholdStatus | null>(null);

  function apply(next: VedlikeholdStatus) {
    // No-op if it's already the current status — just close.
    if (next === task.status) {
      onClose();
      return;
    }
    setSubmitError(null);
    setPending(next);
    const handlers = {
      onSuccess: () => onClose(),
      onError: (e: unknown) => {
        setSubmitError(e instanceof Error ? e.message : nb.vedlikehold.statusModal.errorTitle);
        setPending(null);
      },
    };
    if (next === 'åpen') {
      // åpen is reached by clearing the assignee, never via the status endpoint.
      assignMutation.mutate({ id: task.id, assigneeUserId: null }, handlers);
    } else {
      statusMutation.mutate({ id: task.id, status: next }, handlers);
    }
  }

  return (
    <Modal open={true} onClose={onClose} title={nb.vedlikehold.statusModal.title}>
      <p className={s.modalHelp}>{nb.vedlikehold.statusModal.help}</p>

      <div className={s.statusOptions} role="group" aria-label={nb.vedlikehold.statusModal.title}>
        {STATUS_ORDER.map((value) => {
          const isCurrent = value === task.status;
          return (
            <button
              key={value}
              type="button"
              className={s.statusOption}
              aria-current={isCurrent || undefined}
              aria-busy={pending === value || undefined}
              disabled={pending !== null}
              onClick={() => apply(value)}
            >
              <StatusBadge status={value} />
              {isCurrent && (
                <span className={s.statusCurrentHint}>
                  {nb.vedlikehold.statusModal.currentHint}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {submitError && (
        <p role="alert" className={s.submitError}>
          {submitError}
        </p>
      )}
    </Modal>
  );
}
